import React from 'react';
import type { ColumnDef } from '@tanstack/react-table';

import TruncatedCell from 'components/partials/table-component/truncated-cell/truncated-cell';

import { EMPTY_VALUE } from '../results-step.constants';

import type { CoaTableRow } from '../helpers/build-coa-rows';

interface GetCoaTableSettingsArgs {
  previousYearLabel: string;
  currentYearLabel: string;
}

type CellInfo = { row: { original: CoaTableRow } };
type AmountField = 'previousYearAmount' | 'currentYearAmount';

const formatCoaCurrency = (value: number): string =>
  Math.round(value).toLocaleString('en-US');

const formatCoaPercent = (current: number, previous: number): string =>
  previous
    ? `${Number((((current - previous) / previous) * 100).toFixed(2))}%`
    : EMPTY_VALUE;

const bodyCellClass = 'text-base text-gray-700';

const renderNameCell = (row: CoaTableRow): React.ReactNode =>
  row.rowType === 'section' ? (
    <span className="text-base font-semibold text-navy-800">{row.label}</span>
  ) : (
    <TruncatedCell value={row.name} />
  );

const renderAmountCell = (
  row: CoaTableRow,
  field: AmountField
): React.ReactNode => {
  if (row.rowType === 'section') return null;

  return (
    <span className="flex gap-1.5">
      <span>$</span>
      <span>{formatCoaCurrency(row[field])}</span>
    </span>
  );
};

const renderPercentCell = (row: CoaTableRow): React.ReactNode =>
  row.rowType === 'section'
    ? null
    : formatCoaPercent(row.currentYearAmount, row.previousYearAmount);

export const getCoaTableSettings = ({
  previousYearLabel,
  currentYearLabel,
}: GetCoaTableSettingsArgs): ColumnDef<CoaTableRow, unknown>[] => [
  {
    id: 'name',
    header: () => <span>Parameters</span>,
    cell: (info: CellInfo) => renderNameCell(info.row.original),
    headerClassName: 'rounded-tl-md rounded-bl-md text-sm',
    className: `w-[36%] max-w-0 p-3 ${bodyCellClass}`,
    enableSorting: false,
  },
  {
    id: 'previousYearAmount',
    header: () => <span>{previousYearLabel}</span>,
    cell: (info: CellInfo) =>
      renderAmountCell(info.row.original, 'previousYearAmount'),
    headerClassName: 'text-sm',
    className: `w-[22%] p-3 ${bodyCellClass}`,
    enableSorting: false,
  },
  {
    id: 'currentYearAmount',
    header: () => <span>{currentYearLabel}</span>,
    cell: (info: CellInfo) =>
      renderAmountCell(info.row.original, 'currentYearAmount'),
    headerClassName: 'text-sm',
    className: `w-[21%] p-3 ${bodyCellClass}`,
    enableSorting: false,
  },
  {
    id: 'percentChange',
    header: () => <span>% Change</span>,
    cell: (info: CellInfo) => renderPercentCell(info.row.original),
    headerClassName: 'rounded-tr-md rounded-br-md text-sm',
    className: `w-[22%] p-3 ${bodyCellClass}`,
    enableSorting: false,
  },
];
